import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Cross2Icon } from '@radix-ui/react-icons'

import { cn } from '@/lib/utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <div
    data-cy={`dialog-overlay`}
    ref={ref}
    className={cn(
      `fixed inset-0 z-50 hidden bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 md:block`,
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    overlayClassname?: string | undefined
  }
>(({ className, children, overlayClassname, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassname} />
    <div
      // `pb-[var(--kbd-inset,0px)]` shrinks the items-end alignment zone
      // by the soft-keyboard inset on mobile, so the dialog docks above
      // the keyboard instead of behind it. The var is opt-in via
      // `useKeyboardInsetCssVar()` — defaults to 0px (no behavior change)
      // when no consumer has mounted the hook.
      className={`fixed inset-0 z-[1050] flex items-end justify-center pb-[var(--kbd-inset,0px)] md:items-center md:pb-0`}
    >
      <DialogPrimitive.Content
        data-cy={`dialog-content`}
        id={`dialog-content`}
        ref={ref}
        className={cn(
          `relative mx-auto grid h-[calc(100svh-var(--mobileToBarHeight)-var(--safe-area-inset-top))] w-full gap-4 overflow-y-auto border bg-background p-4 pb-[max(1rem,var(--safe-area-inset-bottom))] shadow-lg data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg md:h-auto md:w-[min(80vw,1000px)] md:pb-4 md:data-[state=open]:animate-in`,
          `data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom`,
          // `focus-within:overflow-hidden`,
          // `data-[state=open]:pt-0 data-[state=closed]:pt-[100vh] delay-200 duration-500 `,
          className,
        )}
        onOpenAutoFocus={e => e.preventDefault()}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          id={`dialog-btn-close`}
          data-cy={`dialog-btn-close`}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <Cross2Icon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </div>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
